import { useFileConverter } from '@centric-os/helpers';
import type {
  FileUpload,
  Integration,
  IntegrationContent,
  IntegrationData,
  IntegrationToggle,
  RecordEntityId,
  StationBrand,
  StationOverview,
  Integrations,
  StationPrivateConfig,
  StationPublicConfig,
} from '@centric-os/types';
import { IntegrationPartner, TypeOfIntegration } from '@centric-os/types';
import type {
  Integration as BEIntegration,
  Content,
  Data,
  GetCentricosStationResponse,
  Integrations as BEIntegrations,
  PutCentricosStationIntegrationBody,
  Toggle,
} from '@compassdigital/sdk.typescript/interface/centricos';
import { assign, cloneDeep, isEmpty } from 'lodash-es';
import { defineStore, type StateTree } from 'pinia';
import { useStationConfigStore } from '../use-station-config-store';

/**
 * Interface representing the state of the store.
 */
interface State extends StateTree {
  editStationInfo: boolean;
  stationBrand: StationBrand;
  imageFieldDirty: boolean;
  stationOverview: StationOverview;
}

/**
 * Creates a store for managing station data.
 *
 * @param {string} [storeId='station'] - The ID of the store.
 * @returns {object} - The store instance.
 */
export function useStationStore(storeId = 'station') {
  const store = defineStore(storeId, {
    state: (): State => ({
      editStationInfo: false,
      stationBrand: null,
      imageFieldDirty: false,
      stationOverview: null,
    }),
    actions: {
      /**
       * Fetches the station brand data by ID.
       *
       * @param {RecordEntityId} id - The ID of the station brand.
       * @returns {Promise<void>} - A promise that resolves when the data is fetched.
       */
      async getStationBrand(id: RecordEntityId): Promise<void> {
        const response = await this.serviceApi.get_location_brand(String(id), {
          query: { nocache: true },
        });
        // @ts-expect-error FIXME: SDK Brand vs hand-maintained StationBrand (TS2322)
        this.stationBrand = response;
      },

      /**
       * Cleans up software integrations in the payload.
       *
       * @param {Partial<StationBrand>} payload - The station brand payload.
       * @returns {Promise<void>} - A promise that resolves when the cleanup is complete.
       */
      async cleanUpSoftwareIntegrations(payload: Partial<StationBrand>): Promise<void> {
        if (!payload.meta?.software_integrations) return;

        if (!payload.meta.software_integrations.time2eat?.enabled) {
          delete payload.meta.software_integrations.time2eat;
        }
      },

      /**
       * Creates a new station.
       *
       * @param {Partial<StationBrand>} payload - The station brand payload.
       * @returns {Promise<StationBrand>} - The created station brand.
       */
      async createStation(payload: Partial<StationBrand>): Promise<StationBrand> {
        // @ts-expect-error FIXME: hand-maintained StationBrand vs SDK Brand body (TS2345)
        const response = await this.serviceApi.post_location_brand(payload);
        // @ts-expect-error FIXME: SDK Brand vs hand-maintained StationBrand (TS2322)
        this.stationBrand = response;
        return this.stationBrand;
      },

      /**
       * Updates the station brand details, including handling image uploads and cleanup.
       *
       * @param {Partial<StationBrand>} payload - The payload containing station brand details.
       * @param {boolean} [updateState=false] - Whether to update the local state after the API call.
       * @returns {Promise<void>} - Resolves when the update is complete.
       * @throws {Error} - Throws an error if no station is selected.
       */
      async updateStationBrand(payload: Partial<StationBrand>, updateState = false): Promise<void> {
        if (!payload?.id) {
          throw new Error('No station selected');
        }

        const newPayload = cloneDeep(payload);
        this.cleanUpSoftwareIntegrations(newPayload);

        let imagePayload: FileUpload | undefined;

        if (this.imageFieldDirty) {
          imagePayload = payload.style?.logo
            ? await this.saveImageFile(payload.style.logo)
            : undefined;

          if (!payload.style?.logo) {
            assign(newPayload.style, { logo: null, alt_logo: null, main_logo: null });
          }
        }

        if (imagePayload?.url) {
          assign(newPayload.style, {
            logo: imagePayload.url,
            alt_logo: imagePayload.url,
            main_logo: imagePayload.url,
          });
        }

        const finalPayload = this.imageFieldDirty ? newPayload : payload;

        if (isEmpty(finalPayload.device_mapping)) {
          delete finalPayload.device_mapping;
        }

        const response = await this.serviceApi.put_location_brand(
          String(payload.id),
          // @ts-expect-error FIXME: hand-maintained StationBrand vs SDK Brand body (TS2345)
          finalPayload,
        );
        if (updateState) {
          // @ts-expect-error FIXME: SDK Brand vs hand-maintained StationBrand (TS2322)
          this.stationBrand = response;
        }
      },

      /**
       * Partially updates the station brand using HTTP PATCH.
       *
       * @param {Partial<StationBrand>} payload - Station brand fields to patch.
       * @param {boolean} [updateState=false] - Whether to patch local state after API call.
       * @returns {Promise<void>} - Resolves when the patch is complete.
       * @throws {Error} - Throws an error if no station ID is provided.
       */
      async patchStationBrand(payload: Partial<StationBrand>, updateState = false): Promise<void> {
        if (!payload?.id) {
          throw new Error('No station selected');
        }
        // Optionally handle image logic or additional steps here if needed
        const response = await this.serviceApi.patch_location_brand(
          String(payload.id),
          // @ts-expect-error FIXME: hand-maintained StationBrand vs SDK Brand body (TS2345)
          payload,
        );

        if (updateState) {
          // @ts-expect-error FIXME: SDK Brand vs hand-maintained StationBrand (TS2322)
          this.stationBrand = response;
        }
      },

      /**
       * Saves an image file and uploads it.
       *
       * @param {File} payload - The image file to save.
       * @returns {Promise<FileUpload>} - The uploaded file details.
       * @throws {Error} - Throws an error if no file is selected.
       */
      async saveImageFile(payload: File): Promise<FileUpload> {
        if (!payload) {
          throw new Error('No file selected');
        }

        const { getBase64 } = useFileConverter();
        const base64 = (await getBase64(payload)) as string;
        const response = await this.serviceApi.post_file({
          file_base64: base64.split(',')?.[1],
        });
        return response;
      },

      /**
       * Deletes a station by ID.
       *
       * @param {RecordEntityId} stationId - The ID of the station to delete.
       * @returns {Promise<void>} - Resolves when the station is deleted.
       */
      async deleteStation(stationId: RecordEntityId): Promise<void> {
        await this.serviceApi.delete_centricos_station(String(stationId));
      },

      /**
       * Fetches station details for rendering the station page.
       *
       * @param {RecordEntityId} stationId - The ID of the station.
       * @returns {Promise<void>} - Resolves when the data is fetched.
       */
      async getStationOverview(stationId: RecordEntityId): Promise<void> {
        try {
          const response = await this.serviceApi.get_centricos_station(String(stationId));
          this.stationOverview = this.mapStationResponseToOverview(response);
        } catch (error) {
          console.error('Error fetching station overview:', error);
        }
      },

      /**
       * Maps a `GetCentricosStationResponse` to a `StationOverview`.
       *
       * @param {GetCentricosStationResponse} response - The raw station response.
       * @returns {StationOverview} - The mapped station overview.
       */
      mapStationResponseToOverview(response: GetCentricosStationResponse): StationOverview {
        const mapToggles = (toggles?: Toggle[]): IntegrationToggle[] | undefined =>
          toggles?.map(({ name, label, value }) => ({ name, label, value }));

        const mapData = (data?: Data[]): IntegrationData[] | undefined =>
          data?.map(({ name, label, value }) => ({ name, label, value }));

        const mapContent = (content?: Content): IntegrationContent | undefined =>
          content
            ? {
                label: content.label,
                toggles: mapToggles(content.toggles),
                data: mapData(content.data),
              }
            : undefined;

        const mapIntegration = (integration?: BEIntegration): Integration | undefined =>
          integration
            ? {
                id: integration.id,
                label: integration.label,
                isIntegrated: integration.is_integrated,
                isEnabled: integration.is_enabled,
                canEnable: integration.can_enable,
                isVisible:
                  (integration as BEIntegration & { is_visible?: boolean }).is_visible ?? true, // Default to true if BE hasn't deployed is_visible yet
                content: mapContent(integration.content),
              }
            : undefined;

        const mapIntegrations = (integrations: BEIntegrations): Integrations => ({
          thrive: mapIntegration(integrations.thrive) || null,
          boost: mapIntegration(integrations.boost) || null,
          nextep: mapIntegration(integrations.nextep) || null,
          volante: mapIntegration(integrations.volante) || null,
          agilysys: mapIntegration(integrations.agilysys) || null,
          agilysys_dod: mapIntegration(integrations.agilysys_dod) || null,
          mashgin: mapIntegration(integrations.mashgin) || null,
          centric_hub: mapIntegration(integrations.centric_hub) || null,
          grubhub: mapIntegration(integrations.grubhub) || null,
          ryst: mapIntegration(integrations.ryst) || null,
          illumia: mapIntegration(integrations.illumia) || null,
        });

        return {
          id: response.id,
          name: response.name,
          stationLocation: response.station_location,
          businessUnit: response.business_unit,
          integrations: mapIntegrations(response.integrations),
        };
      },

      /**
       * Updates the station brand and its configurations.
       *
       * @param {Partial<StationBrand>} stationPayload - The station brand details.
       * @param {Partial<StationPrivateConfig>} privateConfigPayload - The private configuration details.
       * @param {Partial<StationPublicConfig>} publicConfigPayload - The public configuration details.
       * @param {boolean} [updateState=false] - Whether to update the local state.
       * @returns {Promise<void>} - Resolves when updates are complete.
       */
      async updateStationAndConfigurations(
        stationPayload: Partial<StationBrand>,
        privateConfigPayload: Partial<StationPrivateConfig>,
        publicConfigPayload: Partial<StationPublicConfig>,
        updateState = false,
      ): Promise<void> {
        const stationConfigStore = useStationConfigStore();
        await Promise.all([
          this.updateStationBrand(stationPayload, updateState),
          stationConfigStore.saveStationConfig(
            stationPayload.id,
            privateConfigPayload,
            publicConfigPayload,
            updateState,
          ),
        ]);
      },

      /**
       * Deletes a station app integration (Boost or Thrive) via the dedicated DELETE endpoint.
       *
       * @param {RecordEntityId} stationId - The ID of the station.
       * @param {IntegrationPartner} integrationType - The app integration type (Boost/Thrive) to disconnect.
       * @returns {Promise<void>} - Resolves when the integration is deleted.
       * @throws {Error} - Throws if the API returns `success: false` or a network error occurs.
       */
      async deleteAppStationIntegration(
        stationId: RecordEntityId,
        integrationType: IntegrationPartner,
      ): Promise<void> {
        const allowed: string[] = [IntegrationPartner.BOOST, IntegrationPartner.THRIVE];
        if (!allowed.includes(integrationType)) {
          throw new Error(
            `deleteAppStationIntegration only supports Boost/Thrive, received: ${integrationType}`,
          );
        }
        const response = await this.serviceApi.delete_centricos_station_integration(
          String(stationId),
          TypeOfIntegration[integrationType.toLowerCase() as 'boost' | 'thrive'].value,
        );
        if (!response?.success) {
          throw new Error(`Failed to disconnect ${integrationType}`);
        }
      },

      /**
       * Updates the station integration with edge service, and assigns the site to the appropriate multigroup.
       * Only Boost/Thrive are supported for now.
       * Other integrations will be supported in the future.
       *
       * @param {Partial<StationBrand>} stationPayload - The station brand details.
       * @param {IntegrationPartner} integrationType - The integration type.
       * @param {PutCentricosStationIntegrationBody} [body={}] - Configuration sections to save; empty records the integration only.
       * @returns {Promise<void>} - Resolves when the integration is updated.
       * @throws {Error} - Throws if a configuration save returns `success: false`.
       */
      async updateStationIntegration(
        stationPayload: Partial<StationBrand>,
        integrationType: IntegrationPartner,
        body: PutCentricosStationIntegrationBody = {},
      ): Promise<void> {
        switch (integrationType) {
          case IntegrationPartner.BOOST:
          case IntegrationPartner.THRIVE: {
            const response = await this.serviceApi.put_centricos_station_integration(
              String(stationPayload.id),
              TypeOfIntegration[integrationType.toLowerCase() as 'boost' | 'thrive'].value,
              body,
            );
            // Throw only for config saves; the connect flow (empty body) has always ignored the response.
            if (!isEmpty(body) && !response?.success) {
              throw new Error(`Failed to save the ${integrationType} configuration`);
            }
            break;
          }
          default:
            break;
        }
      },
    },
    getters: {},
    pagination: false,
  });
  return store();
}
